import { useState, useEffect, useRef } from "react";
import { Gamepad2, Star, Flame, TrendingUp, TrendingDown } from "lucide-react";
import { C, GAME_STOCKS, GAME_TYPES } from '../constants';
import { shuffleArr } from '../utils/helpers';

export default function LoadingGame({ stage, onPoints }) {
  const TIMER_SECS = 15;
  const [score,       setScore]       = useState(0);
  const [streak,      setStreak]      = useState(0);
  const [best,        setBest]        = useState(() => { try { return parseInt(localStorage.getItem("hma_game_best")||"0"); } catch { return 0; } });
  const [round,       setRound]       = useState(0);
  const [gameType,    setGameType]    = useState(() => GAME_TYPES[Math.floor(Math.random()*GAME_TYPES.length)]);
  const [stocks]      = useState(() => shuffleArr(GAME_STOCKS));
  const [stockIdx,    setStockIdx]    = useState(0);
  const [answered,    setAnswered]    = useState(false);
  const [correct,     setCorrect]     = useState(null);
  const [sliderVal,   setSliderVal]   = useState(null);
  const [sliderLocked,setSliderLocked]= useState(false);
  const [timeLeft,    setTimeLeft]    = useState(TIMER_SECS);
  const [flash,       setFlash]       = useState(null);
  const [scoreAnim,   setScoreAnim]   = useState(false);
  const [newBest,     setNewBest]     = useState(false);
  const [ptsEarned,   setPtsEarned]   = useState(null);
  const [nextIn,      setNextIn]      = useState(null);
  const timerRef = useRef(null);
  const nextRef  = useRef(null);

  const s  = stocks[stockIdx % stocks.length];
  const s2 = stocks[(stockIdx + 1) % stocks.length];

  const sliderMin = Math.round(s.yearAgo * 0.4);
  const sliderMax = Math.round(s.yearAgo * 2.5);
  const currentSlider = sliderVal ?? s.yearAgo;
  const sliderError = sliderLocked ? Math.abs(currentSlider - s.actual) : null;

  useEffect(() => {
    if (answered) return;
    setTimeLeft(TIMER_SECS);
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); handleAnswer(false); return 0; } // eslint-disable-line
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [round]); // eslint-disable-line

  const advance = () => {
    setStockIdx(i => i + 2);
    setGameType(t => GAME_TYPES[(GAME_TYPES.indexOf(t)+1) % GAME_TYPES.length]);
    setAnswered(false); setCorrect(null); setSliderVal(null); setSliderLocked(false);
    setPtsEarned(null); setNextIn(null); setRound(r => r+1);
  };

  const handleAnswer = (isCorrect) => {
    clearInterval(timerRef.current);
    const multiplier = Math.min(streak + 1, 5);
    const speedBonus = isCorrect ? Math.floor(timeLeft / 3) : 0;
    const pts = isCorrect ? multiplier + speedBonus : 0;
    setAnswered(true); setCorrect(isCorrect);
    setFlash(isCorrect ? "green" : "red");
    setTimeout(() => setFlash(null), 800);
    if (isCorrect) {
      setPtsEarned(pts);
      setScore(sc => {
        const ns = sc + pts;
        if (ns > best) { setBest(ns); setNewBest(true); try { localStorage.setItem("hma_game_best", String(ns)); } catch {} }
        return ns;
      });
      setStreak(st => st + 1);
      setScoreAnim(true); setTimeout(() => setScoreAnim(false), 600);
      if (onPoints) onPoints(pts);
    } else { setStreak(0); setNewBest(false); }
    // Countdown to next round
    let countdown = 3;
    setNextIn(countdown);
    nextRef.current = setInterval(() => {
      countdown--;
      if (countdown <= 0) { clearInterval(nextRef.current); advance(); }
      else setNextIn(countdown);
    }, 1000);
  };

  const streakMsg = streak >= 10 ? "👑 LEGENDARY" : streak >= 7 ? "🔥 UNSTOPPABLE!" : streak >= 5 ? "⚡ ON FIRE!" : streak >= 3 ? "🚀 STREAK!" : null;
  const correctMsgs = ["✓ Nailed it!", "✓ Sharp!", "✓ Market genius!", "✓ Correct!", "✓ You got it!"];
  const wrongMsgs   = ["✗ Not quite", "✗ Swing and a miss", "✗ The market humbles us all", "✗ Wrong!", "✗ Ouch"];
  const resultMsg = correct
    ? (streak >= 5 ? "🔥 " : "") + correctMsgs[round % correctMsgs.length]
    : wrongMsgs[round % wrongMsgs.length];

  const btnBase = { border:"none", borderRadius:14, fontWeight:800, cursor:"pointer", transition:"all .15s", fontFamily:"'Inter',sans-serif" };
  const gameLabel = gameType === "higher-lower" ? "Higher or Lower" : gameType === "overvalued" ? "Overvalued?" : "Beat the Analyst";
  const multiplier = Math.min(streak + 1, 5);
  const timerPct = (timeLeft / TIMER_SECS) * 100;
  const timerColor = timeLeft <= 3 ? C.neg : timeLeft <= 6 ? "#FF9500" : C.green;

  return (
    <div style={{ background:"linear-gradient(145deg,#0D0900,#161000)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:20, overflow:"hidden", marginBottom:20, boxShadow:"0 0 40px rgba(201,168,76,0.07)", position:"relative" }}>
      {flash && <div style={{ position:"absolute", inset:0, borderRadius:20, background: flash==="green" ? "rgba(0,200,5,0.15)" : "rgba(255,59,48,0.18)", zIndex:10, animation:"hmaFlash .8s ease-out forwards", pointerEvents:"none" }}/>}
      <div style={{ height:4, background:"linear-gradient(90deg,#5a3d0e,#e8c875,#ffe87a,#e8c875,#5a3d0e)", backgroundSize:"200% auto", animation:"hmaShimmer 4s linear infinite" }}/>

      <div style={{ padding:"22px 26px" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
          <div>
            <div style={{ fontSize:18, fontWeight:900, color:C.text, letterSpacing:".01em", lineHeight:1.2, display:"flex", alignItems:"center", gap:8 }}><Gamepad2 size={18} strokeWidth={2} color={C.gold2}/>Play While You Wait</div>
            <div style={{ fontSize:11, color:C.hint, marginTop:3 }}>Test your market instincts while we crunch numbers</div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:3 }}>
            <div style={{ fontSize:26, fontWeight:900, lineHeight:1, color: score > 0 ? C.gold2 : "rgba(255,255,255,0.2)", animation: scoreAnim ? "hmaScorePop .6s ease-out" : "none" }}>{score} pts</div>
            {newBest && score > 0 && <span style={{ display:"flex", alignItems:"center", gap:3, fontSize:11, fontWeight:800, textTransform:"uppercase", letterSpacing:".12em", color:C.gold2 }}><Star size={9} fill={C.gold2} strokeWidth={0}/> New Best!</span>}
            {best > 0 && !newBest && <span style={{ fontSize:11, color:C.hint }}>Best: {best}</span>}
          </div>
        </div>

        {/* Streak banner */}
        {streak >= 2 && (
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10, padding:"8px 14px", background:"rgba(255,107,53,0.08)", border:"1px solid rgba(255,107,53,0.2)", borderRadius:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:3 }}>
              {Array.from({length:Math.min(streak,5)}).map((_,i)=><Flame key={i} size={15} strokeWidth={2} color="#FF6B35" fill="rgba(255,107,53,0.4)"/>)}
            </div>
            <div style={{ fontSize:13, fontWeight:900, color:"#FF6B35" }}>{streakMsg || `${streak} streak`}</div>
            <div style={{ marginLeft:"auto", fontSize:13, fontWeight:900, color:C.gold2, background:"rgba(201,168,76,0.15)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:20, padding:"2px 12px" }}>×{multiplier}</div>
          </div>
        )}

        {/* Timer bar */}
        {!answered && (
          <div style={{ marginBottom:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:C.hint, marginBottom:4 }}>
              <span style={{ fontWeight:700, letterSpacing:".08em", textTransform:"uppercase" }}>{gameLabel}</span>
              <span style={{ fontWeight:800, color: timeLeft <= 3 ? C.neg : timeLeft <= 6 ? "#FF9500" : "rgba(255,255,255,0.5)", fontSize:12 }}>{timeLeft}s</span>
            </div>
            <div style={{ height:6, background:"rgba(255,255,255,0.07)", borderRadius:3, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${timerPct}%`, background:timerColor, borderRadius:3, transition:"width 1s linear", boxShadow:`0 0 10px ${timerColor}66` }}/>
            </div>
          </div>
        )}

        {/* Points earned pop */}
        {ptsEarned !== null && answered && correct && (
          <div style={{ textAlign:"center", fontSize:28, fontWeight:900, color:C.gold2, marginBottom:8, animation:"hmaScorePop .6s ease-out" }}>
            +{ptsEarned} pts
            {multiplier > 1 && <span style={{ fontSize:14, color:"#FF6B35", marginLeft:8 }}>×{multiplier} streak</span>}
            {ptsEarned - multiplier > 0 && <span style={{ fontSize:14, color:"rgba(255,255,255,0.5)", marginLeft:6 }}>+{ptsEarned - multiplier} speed</span>}
          </div>
        )}

        {/* Round dots */}
        {answered && (
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontSize:18, fontWeight:900, color: correct ? C.green : C.neg, animation: correct ? "hmaScorePop .4s ease-out" : "hmaShake .4s ease-out" }}>{resultMsg}</div>
            {nextIn !== null && <div style={{ fontSize:12, fontWeight:700, color:C.hint }}>Next in {nextIn}…</div>}
          </div>
        )}

        {/* ── Higher or Lower ── */}
        {gameType === "higher-lower" && (
          <div>
            <div style={{ fontSize:15, fontWeight:600, color:"rgba(255,255,255,0.7)", textAlign:"center", marginBottom:16 }}>Which stock has a higher share price right now?</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {[s, s2].map((stock, i) => {
                const isHigher = stock.price >= (i===0 ? s2.price : s.price);
                return (
                  <button key={stock.ticker} disabled={answered} onClick={() => handleAnswer(isHigher)}
                    style={{ ...btnBase, padding:"22px 14px",
                      background: answered ? (isHigher ? "rgba(0,200,5,0.15)" : "rgba(255,59,48,0.08)") : "rgba(255,255,255,0.05)",
                      border: answered ? (isHigher ? "2px solid rgba(0,200,5,0.5)" : "2px solid rgba(255,59,48,0.25)") : "2px solid rgba(255,255,255,0.1)",
                      cursor: answered ? "default" : "pointer",
                      boxShadow: answered && isHigher ? "0 0 24px rgba(0,200,5,0.15)" : "none",
                      transform: !answered ? "scale(1)" : undefined,
                    }}>
                    <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:44, fontWeight:900, color: answered ? (isHigher ? C.green : "rgba(255,255,255,0.25)") : C.gold2, lineHeight:1, marginBottom:4 }}>{stock.ticker}</div>
                    <div style={{ fontSize:12, color:C.hint, marginBottom:10 }}>{stock.name}</div>
                    {answered
                      ? <div style={{ fontSize:22, fontWeight:900, color: isHigher ? C.green : C.neg }}>${stock.price.toLocaleString()}</div>
                      : <div style={{ fontSize:13, color:C.gold2, fontWeight:700, animation:"hmapulse2 1.6s ease-in-out infinite" }}>Tap to pick →</div>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Is it Overvalued? ── */}
        {gameType === "overvalued" && (
          <div>
            <div style={{ textAlign:"center", marginBottom:18 }}>
              <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:52, fontWeight:900, color:C.gold2, lineHeight:1, marginBottom:4 }}>{s.ticker}</div>
              <div style={{ fontSize:13, color:C.hint, marginBottom:14 }}>{s.name}</div>
              <div style={{ display:"inline-block", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:14, padding:"12px 32px" }}>
                <div style={{ fontSize:40, fontWeight:900, color:C.text, lineHeight:1 }}>{s.pe}x</div>
                <div style={{ fontSize:11, color:C.hint, marginTop:3 }}>Price-to-Earnings Ratio</div>
              </div>
            </div>
            <div style={{ fontSize:15, fontWeight:600, color:"rgba(255,255,255,0.88)", textAlign:"center", marginBottom:14 }}>Your call — overvalued or not?</div>
            {!answered ? (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <button onClick={() => handleAnswer(s.peVerdict === "overvalued")}
                  style={{ ...btnBase, fontSize:15, padding:"18px 12px", background:"rgba(255,59,48,0.09)", border:"2px solid rgba(255,59,48,0.25)", color:C.neg, display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}><TrendingUp size={16} strokeWidth={2}/> Overvalued</button>
                <button onClick={() => handleAnswer(s.peVerdict !== "overvalued")}
                  style={{ ...btnBase, fontSize:15, padding:"18px 12px", background:"rgba(0,200,5,0.08)", border:"2px solid rgba(0,200,5,0.22)", color:C.green, display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}><TrendingDown size={16} strokeWidth={2}/> Fair / Undervalued</button>
              </div>
            ) : (
              <div style={{ textAlign:"center", fontSize:14, fontWeight:600, color:"rgba(255,255,255,0.6)", padding:"14px 0" }}>
                {s.name} at {s.pe}x P/E is <strong style={{ color: s.peVerdict==="overvalued"?C.neg:s.peVerdict==="undervalued"?C.green:C.gold2 }}>{s.peVerdict}</strong> — {s.peVerdict==="overvalued"?"high multiple relative to sector":"trading at a reasonable valuation"}
              </div>
            )}
          </div>
        )}

        {/* ── Beat the Analyst ── */}
        {gameType === "beat-analyst" && (
          <div>
            <div style={{ textAlign:"center", marginBottom:16 }}>
              <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:52, fontWeight:900, color:C.gold2, lineHeight:1, marginBottom:4 }}>{s.ticker}</div>
              <div style={{ fontSize:13, color:C.hint, marginBottom:8 }}>{s.name}</div>
              <div style={{ fontSize:14, color:"rgba(255,255,255,0.82)" }}>One year ago: <span style={{ color:C.text, fontWeight:900, fontSize:20 }}>${s.yearAgo}</span></div>
            </div>
            <div style={{ fontSize:14, fontWeight:600, color:"rgba(255,255,255,0.7)", textAlign:"center", marginBottom:14 }}>Drag to guess today's price — within 15% wins 🎯</div>
            <input type="range" min={sliderMin} max={sliderMax} value={currentSlider} disabled={sliderLocked}
              onChange={e => setSliderVal(Number(e.target.value))}
              style={{ width:"100%", accentColor:"#e8c875", height:8, marginBottom:8, cursor: sliderLocked ? "default" : "pointer" }}
            />
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:C.hint, marginBottom:16 }}>
              <span>${sliderMin}</span>
              <span style={{ fontSize:26, fontWeight:900, color:C.gold2 }}>${currentSlider.toLocaleString()}</span>
              <span>${sliderMax}</span>
            </div>
            {!sliderLocked ? (
              <button onClick={() => { setSliderLocked(true); handleAnswer(Math.abs(currentSlider - s.actual) / s.actual < 0.15); }}
                style={{ ...btnBase, width:"100%", padding:"16px 0", background:"linear-gradient(135deg,#e8c875,#ffe87a)", color:"#000", borderRadius:12, fontSize:16 }}>
                Lock In ${currentSlider.toLocaleString()} →
              </button>
            ) : (
              <div style={{ textAlign:"center", fontSize:14, fontWeight:600, color:"rgba(255,255,255,0.6)", paddingTop:4 }}>
                Actual price: <strong style={{ color:C.text, fontSize:18 }}>${s.actual.toLocaleString()}</strong>
                {sliderError !== null && <span style={{ color:C.hint, fontWeight:400, fontSize:12, display:"block", marginTop:4 }}>You were off by ${sliderError} ({Math.round(sliderError/s.actual*100)}%)</span>}
              </div>
            )}
          </div>
        )}

        {/* Stage label */}
        {stage && (
          <div style={{ marginTop:18, paddingTop:14, borderTop:"1px solid rgba(255,255,255,0.05)", display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ display:"flex", gap:4 }}>
              {[0,1,2].map(i=><div key={i} style={{ width:5, height:5, borderRadius:"50%", background:C.gold2, animation:`hmapulse 1.2s ${i*0.22}s ease-in-out infinite` }}/>)}
            </div>
            <span style={{ fontSize:11, color:C.hint, letterSpacing:".04em" }}>{stage}</span>
          </div>
        )}
      </div>
    </div>
  );
}
